/**
 * MFA enrolment.
 *
 * Reached by a restricted session: the user authenticated with a password but
 * their role requires a second factor they have not set up yet. Until the code
 * is confirmed, the API refuses every other route.
 */

import { useEffect, useState, type FormEvent } from 'react'

import { api } from '../api/cliente'
import { Aviso, Botao, Campo, Carregando } from '../componentes/ui'
import { MarcaEgba } from '../componentes/marca'
import { useAuth } from '../estado/auth'

interface RespostaIniciar {
  segredo: string
  qrCode: string
}

export function ConfigurarMfa() {
  const { usuario, recarregar, sair } = useAuth()

  const [dados, setDados] = useState<RespostaIniciar | null>(null)
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [mostrarSegredo, setMostrarSegredo] = useState(false)

  useEffect(() => {
    let cancelado = false
    void (async () => {
      try {
        const r = await api.post<RespostaIniciar>('/api/v1/auth/mfa/iniciar')
        if (!cancelado) setDados(r)
      } catch (falha) {
        if (!cancelado) {
          setErro(falha instanceof Error ? falha.message : 'Não foi possível iniciar o cadastro.')
        }
      }
    })()
    return () => {
      cancelado = true
    }
  }, [])

  async function aoConfirmar(e: FormEvent) {
    e.preventDefault()
    setErro(null)
    setEnviando(true)
    try {
      await api.post('/api/v1/auth/mfa/confirmar', { codigo })
      await recarregar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Código inválido.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-8 flex justify-center">
          <MarcaEgba variante="completo" altura={48} />
        </div>

        <div className="cartao p-8">
          <h1 className="text-xl font-semibold text-slate-900">Ative a verificação em duas etapas</h1>
          <p className="mt-1.5 text-sm text-slate-500">
            O perfil <strong className="font-medium text-slate-700">{usuario?.nome}</strong> tem
            permissão para alterar preços e acessar dados sensíveis, então exige um segundo fator.
          </p>

          {!dados && !erro && <Carregando texto="Gerando seu código…" />}

          {dados && (
            <>
              <ol className="mt-6 space-y-5 text-sm text-slate-600">
                <li className="flex gap-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-marca-50 text-xs font-semibold text-marca-700">
                    1
                  </span>
                  <span>
                    Instale um aplicativo autenticador — Google Authenticator, Microsoft
                    Authenticator, 1Password ou similar.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-marca-50 text-xs font-semibold text-marca-700">
                    2
                  </span>
                  <div className="min-w-0 flex-1">
                    <p>Escaneie o código abaixo:</p>
                    <div className="mt-3 flex justify-center rounded-lg border border-slate-200 bg-white p-4">
                      <img
                        src={dados.qrCode}
                        alt="QR Code para cadastrar o aplicativo autenticador"
                        className="size-44"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setMostrarSegredo((v) => !v)}
                      className="mt-2 text-xs text-marca-600 underline hover:text-marca-700"
                    >
                      {mostrarSegredo ? 'Ocultar' : 'Não consigo escanear — mostrar código manual'}
                    </button>
                    {mostrarSegredo && (
                      <p className="mt-2 rounded-md bg-slate-100 px-3 py-2 font-mono text-xs break-all text-slate-700">
                        {dados.segredo}
                      </p>
                    )}
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-marca-50 text-xs font-semibold text-marca-700">
                    3
                  </span>
                  <span className="flex-1">Digite o código de 6 dígitos que o aplicativo mostrar:</span>
                </li>
              </ol>

              <form onSubmit={aoConfirmar} className="mt-6 space-y-4">
                <Campo
                  rotulo="Código de verificação"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={7}
                  required
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  placeholder="000000"
                  className="text-center text-lg tracking-[0.4em]"
                />

                {erro && <Aviso tom="perigo">{erro}</Aviso>}

                <Botao type="submit" tamanho="lg" carregando={enviando} className="w-full">
                  Ativar e continuar
                </Botao>
              </form>
            </>
          )}

          {erro && !dados && <Aviso tom="perigo">{erro}</Aviso>}

          <button
            type="button"
            onClick={() => void sair()}
            className="mt-6 w-full text-center text-xs text-slate-400 hover:text-slate-600"
          >
            Sair e entrar com outra conta
          </button>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          Guarde o código manual num gerenciador de senhas. Sem ele e sem o aplicativo, será
          necessário pedir a um administrador para reconfigurar seu acesso.
        </p>
      </div>
    </main>
  )
}
